/**
 * Shared admin action utilities used by AdminDatesPage and AdminComicsPage.
 * Extracted to avoid duplicating video preview, email, and block/unblock logic.
 *
 * @see docs/APP_UI_SPEC.md — B1 (spot + bringer + mailto), B2 (callers: Events & Comics pages), B3 (booking history), B4 (in-app Gmail send), C4 (video + bio preview)
 */

import Swal from 'sweetalert2';
import { supabase } from '../supabaseClient';
import { getVideoEmbedUrl } from './videoEmbed.js';
import { buildVideoPreviewHtml, videoPreviewPopupWidth } from './videoPreview.js';
import {
  BOOKING_SPOT_SWAL_OPTIONS,
  defaultBringerCheckboxForSpot,
} from './bookingSpot.js';
import {
  buildBookingHistoryContextHtml,
  findMostRecentRecordedSpot,
  spotOfferMismatchWarning,
} from './comedianBookingHistory.js';
import {
  composeBookingOffer,
  generateMailtoLink,
} from './bookingEmail.js';

export {
  composeBookingOffer,
  generateBookingEmailBody,
  generateMailtoLink,
  generateBookingSubject,
  openBookingMailtoLink,
  SUNSET_BOOKING_MAILBOX,
} from './bookingEmail.js';

function buildSpotTypePickerHtml(historyHtml = '') {
  const rows = Object.entries(BOOKING_SPOT_SWAL_OPTIONS)
    .map(
      ([value, label]) =>
        `<label class="booking-spot-type-row"><input type="radio" name="swal-booking-spot" value="${value}" /><span class="booking-spot-type-row-label">${label}</span></label>`
    )
    .join('');
  const picker = `<p class="booking-spot-type-hint">For 5's you can set bringer in the next step.</p><div class="booking-spot-type-options" role="radiogroup" aria-label="Spot type">${rows}</div>`;
  return `${historyHtml}${picker}`;
}

async function promptSpotTypeForBooking(historyHtml = '') {
  const { value, isConfirmed } = await Swal.fire({
    title: 'Spot type for this booking',
    html: buildSpotTypePickerHtml(historyHtml),
    width: '28rem',
    showCancelButton: true,
    confirmButtonText: 'Next',
    cancelButtonText: 'Cancel',
    customClass: { popup: 'booking-spot-type-popup' },
    preConfirm: () => {
      const checked = Swal.getPopup()?.querySelector('input[name="swal-booking-spot"]:checked');
      if (!checked) {
        Swal.showValidationMessage('Choose a spot type');
        return false;
      }
      return checked.value;
    },
  });
  if (!isConfirmed || value == null || value === '') return null;
  return { spotCode: String(value) };
}

async function promptBringerForFiveMinuteSpot(venues, historyHtml = '') {
  const defBringer = defaultBringerCheckboxForSpot(venues, '5');
  const venueHint = venues?.venue_name ? ` (${venues.venue_name})` : '';

  const { value: bringerPayload, isConfirmed: ok2 } = await Swal.fire({
    title: 'Bringer?',
    html: `${historyHtml}<p class="booking-bringer-intro">A <strong>bringer</strong> means you bring an audience member to the show. Default is <strong>checked</strong> at Sunset &amp; Studio48; <strong>off</strong> at Craic-Up and elsewhere${venueHint}.</p>
<label class="booking-bringer-check" for="swal-booking-bringer"><input type="checkbox" id="swal-booking-bringer" ${defBringer ? 'checked' : ''} /><span>This booking includes <strong>bringer</strong> duties (bring an audience member)</span></label>`,
    width: '28rem',
    showCancelButton: true,
    confirmButtonText: 'Compose email',
    cancelButtonText: 'Cancel',
    focusConfirm: false,
    customClass: { popup: 'booking-bringer-popup' },
    // Must not return `false` when unchecked — SweetAlert2 treats that as "block close" (B1 regression).
    preConfirm: () => {
      const el = document.getElementById('swal-booking-bringer');
      return { isBringer: Boolean(el?.checked) };
    },
  });

  if (!ok2) return null;
  return { isBringer: Boolean(bringerPayload?.isBringer) };
}

/**
 * Show a video preview popup for an application's video link, with act details
 * (email / socials / notes) beside the player so bookers can read while watching.
 * Falls back to opening in a new tab for unrecognised platforms.
 *
 * @param {{ comedian_name: string, video_link: string, email?: string, facebook?: string, instagram?: string, notes?: string }} application
 */
export function previewVideo(application) {
  const embedUrl = getVideoEmbedUrl(application.video_link);

  if (!embedUrl) {
    window.open(application.video_link, '_blank', 'noopener');
    return;
  }

  Swal.fire({
    title: application.comedian_name,
    html: buildVideoPreviewHtml(application, embedUrl),
    width: videoPreviewPopupWidth,
    showCloseButton: true,
    showConfirmButton: false,
    customClass: { popup: 'video-preview-popup' }
  });
}

async function confirmSpotMismatchIfNeeded(mostRecent, pick) {
  const warning = spotOfferMismatchWarning(mostRecent, pick);
  if (!warning) return true;
  const { isConfirmed } = await Swal.fire({
    icon: 'warning',
    title: 'Spot length mismatch',
    text: warning,
    showCancelButton: true,
    confirmButtonText: 'Continue anyway',
    cancelButtonText: 'Go back',
  });
  return isConfirmed;
}

/**
 * Ask spot length / headliner (5 / 7 / 10 / headliner), then **only for 5** whether it is a **bringer** spot
 * (bring an audience member). 7, 10, and headliner skip the bringer step (`isBringer` false).
 * Checkbox default for 5: Sunset & Studio48 on, Craic-Up and others off (`defaultBringerCheckboxForSpot`).
 *
 * @param {{ venues?: { venue_slug?: string, venue_name?: string } }} event
 * @param {{ comedianName?: string, bookingHistoryRows?: object[] }} [options] — B3 history context
 * @returns {Promise<{ spotCode: string, isBringer: boolean }|null>} null if cancelled
 */
export async function promptBookingSpotAndBringer(event, options = {}) {
  const venues = event?.venues || {};
  const { comedianName, bookingHistoryRows = [] } = options;
  const historyHtml = buildBookingHistoryContextHtml(bookingHistoryRows, comedianName);
  const mostRecent = findMostRecentRecordedSpot(bookingHistoryRows);

  const pick = await promptSpotTypeForBooking(historyHtml);
  if (!pick) return null;

  const { spotCode } = pick;
  let result;
  if (spotCode === 'headliner' || spotCode === '7' || spotCode === '10') {
    result = { spotCode, isBringer: false };
  } else {
    const bri = await promptBringerForFiveMinuteSpot(venues, historyHtml);
    if (!bri) return null;
    result = { spotCode, isBringer: bri.isBringer };
  }

  const ok = await confirmSpotMismatchIfNeeded(mostRecent, result);
  if (!ok) return null;
  return result;
}

/**
 * Generate a mailto link for a booking email using the DB template.
 * Returns the mailto URL string (caller is responsible for navigating to it).
 *
 * @param {{ comedian_name: string, email: string }} application
 * @param {{ event_name: string, event_date: string, venues?: object }} event
 * @param {{ body: string }|null} emailTemplate
 * @param {{ spotCode?: string, isBringer?: boolean }} [options] — from promptBookingSpotAndBringer() (B1)
 * @returns {string} mailto URL
 */
export function sendBookingEmail(application, event, emailTemplate, options = {}) {
  const draft = composeBookingOffer(application, event, emailTemplate, options);
  return generateMailtoLink({
    email: draft.to,
    subject: draft.subject,
    body: draft.body,
    replyTo: draft.replyTo,
  });
}

/**
 * Prompt admin to block a comedian, then insert into blocked_emails.
 * Returns { blocked: true, reason } on success, { blocked: false } on cancel/error.
 *
 * @param {{ comedian_name: string, email: string }} app
 * @returns {Promise<{ blocked: boolean, reason?: string }>}
 */
export async function blockComedian(app) {
  const result = await Swal.fire({
    title: 'Block this comedian?',
    text: `Block "${app.comedian_name}" (${app.email})? Their applications will be hidden and they won't receive confirmation emails.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    confirmButtonText: 'Yes, block them',
    input: 'text',
    inputLabel: 'Reason (optional)',
    inputPlaceholder: 'e.g. inappropriate behaviour'
  });

  if (!result.isConfirmed) return { blocked: false };

  try {
    const { error } = await supabase
      .from('blocked_emails')
      .insert({ email: app.email.toLowerCase(), reason: result.value || null });

    if (error) throw error;

    Swal.fire('Blocked', `${app.comedian_name} has been blocked.`, 'success');
    return { blocked: true, reason: result.value || null };
  } catch (err) {
    console.error('Error blocking comedian:', err);
    Swal.fire('Error', 'Failed to block comedian', 'error');
    return { blocked: false };
  }
}

/**
 * Prompt admin to unblock an email, then delete from blocked_emails.
 * Returns { unblocked: true } on success, { unblocked: false } on cancel/error.
 *
 * @param {{ id: string, email: string }} entry
 * @returns {Promise<{ unblocked: boolean }>}
 */
export async function unblockEmail(entry) {
  const result = await Swal.fire({
    title: 'Unblock this email?',
    text: `Unblock "${entry.email}"? Their applications will become visible again.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Yes, unblock'
  });

  if (!result.isConfirmed) return { unblocked: false };

  try {
    const { error } = await supabase
      .from('blocked_emails')
      .delete()
      .eq('id', entry.id);

    if (error) throw error;

    Swal.fire('Unblocked', `${entry.email} has been unblocked.`, 'success');
    return { unblocked: true };
  } catch (err) {
    console.error('Error unblocking email:', err);
    Swal.fire('Error', 'Failed to unblock email', 'error');
    return { unblocked: false };
  }
}
